import { useCallback, useMemo } from 'react';
import {
  AttachmentUploadPermissions,
  Channel,
  ConnectPermission,
  FlightRoomPermissions,
  FlightRoomSummary,
  MentionPermissions,
  TaskPermissions,
} from '@connect-x-app/shared-domain-models';
import {
  hasAllPermissions,
  hasAnyPermission,
  hasOpenTasks,
  hasPendingAccessRequest,
  hasPermission,
  isActiveMemberOfRoom,
  isPartOfRoom,
} from '@connect-x-app/shared-utils';
import { Authentication } from '@connect-x-app/shared-view-models';

import { useAuthenticationStore } from '../stores';

export const useAuthentication = (): Authentication => {
  const { setUser, user } = useAuthenticationStore();

  const reset = useCallback(() => {
    setUser(null);
  }, [setUser]);

  const internalHasPermission = useCallback(
    (permission: ConnectPermission) => {
      if (!user?.permissions) {
        return false;
      }
      return hasPermission(user.permissions, permission);
    },
    [user?.permissions],
  );

  const internalHasAnyPermission = useCallback(
    (permissions: ConnectPermission[]) => {
      if (!user?.permissions) {
        return false;
      }
      return hasAnyPermission(user.permissions, permissions);
    },
    [user?.permissions],
  );

  const internalHasAllPermissions = useCallback(
    (permissions: ConnectPermission[]) => {
      if (!user?.permissions) {
        return false;
      }
      return hasAllPermissions(user.permissions, permissions);
    },
    [user?.permissions],
  );

  const canJoinFlightRooms = useMemo(() => {
    return internalHasPermission(FlightRoomPermissions.JOIN);
  }, [internalHasPermission]);

  const canMentionExternally = useMemo(() => {
    return internalHasPermission(MentionPermissions.MENTION_EXTERNALLY);
  }, [internalHasPermission]);

  const canRequestAccess = useMemo(() => {
    return internalHasPermission(FlightRoomPermissions.REQUEST_ACCESS);
  }, [internalHasPermission]);

  const canSearchFlightRooms = useMemo(() => {
    return internalHasAnyPermission([FlightRoomPermissions.JOIN, FlightRoomPermissions.REQUEST_ACCESS]);
  }, [internalHasAnyPermission]);

  const canUploadAttachments = useMemo(() => {
    return internalHasAnyPermission([AttachmentUploadPermissions.ALLOW_DOCUMENTS, AttachmentUploadPermissions.ALLOW_IMAGES]);
  }, [internalHasAnyPermission]);

  const canViewFlightRooms = useMemo(() => {
    return internalHasPermission(FlightRoomPermissions.VIEW);
  }, [internalHasPermission]);

  const canViewTasks = useMemo(() => {
    return internalHasPermission(TaskPermissions.VIEW);
  }, [internalHasPermission]);

  const internalIsActiveMemberOfRoom = useCallback(
    (room: FlightRoomSummary | undefined) => {
      if (!user || !room) {
        return false;
      }
      return isActiveMemberOfRoom(room);
    },
    [user],
  );

  const canViewMessages = useCallback(
    (room: FlightRoomSummary) => {
      return (
        internalIsActiveMemberOfRoom(room) ||
        (hasOpenTasks(room.tasks) && canViewTasks && canJoinFlightRooms && canViewFlightRooms)
      );
    },
    [canJoinFlightRooms, canViewFlightRooms, canViewTasks, internalIsActiveMemberOfRoom],
  );

  const hasGrantAccess = useMemo(() => {
    return internalHasPermission(FlightRoomPermissions.GRANT_ACCESS);
  }, [internalHasPermission]);

  const internalHasPendingAccessRequest = useCallback(
    (room: FlightRoomSummary | undefined) => {
      if (!user || !room) {
        return false;
      }

      return hasPendingAccessRequest(room);
    },
    [user],
  );

  const isUserPartOfRoom = useCallback(
    (room: FlightRoomSummary | undefined) => {
      if (!user || !room) {
        return false;
      }
      return isPartOfRoom(room);
    },
    [user],
  );

  const isUserPartOfChannel = useCallback(
    (channel: Channel) => {
      if (!user) {
        return false;
      }
      return !!channel.users.find(u => u.externalId === user.externalId);
    },
    [user],
  );

  return {
    canJoinFlightRooms,
    canMentionExternally,
    canRequestAccess,
    canSearchFlightRooms,
    canUploadAttachments,
    canViewFlightRooms,
    canViewMessages,
    canViewTasks,
    hasAllPermissions: internalHasAllPermissions,
    hasAnyPermission: internalHasAnyPermission,
    hasGrantAccess,
    hasPendingAccessRequest: internalHasPendingAccessRequest,
    hasPermission: internalHasPermission,
    isActiveMemberOfRoom: internalIsActiveMemberOfRoom,
    isUserPartOfChannel,
    isUserPartOfRoom,
    reset,
    setUser,
    user,
  };
};
